import { makeAutoObservable } from 'mobx'
import type AppStore from '../AppStore'
import { type AxiosResponse, type AxiosInstance } from 'axios'
import { CachedClient, LazyAggregates } from 'helpers'
import {
  type JSONObject,
  type VegetationSimulation,
  type VegetationSimulationAggregate,
  type VegetationUserData
} from 'stores/@types'
import {
  type AppFeature,
  FeatureType,
  type MultiFeature,
  type ParentFeature,
  type Project,
  type WriteFeature
} from 'stores/features'

export class Vegetation implements AppFeature, WriteFeature {
  public readonly type = FeatureType.Vegetation
  public readonly title = 'Ein Objekt angewählt'

  private readonly cached: CachedClient
  public readonly isEditingSupported: boolean = true

  public store: AppStore

  constructor(
    private readonly project: Project,
    public uuid: string
  ) {
    makeAutoObservable(this, { uuid: false })
    this.cached = new CachedClient(this)
    this.store = project.store
  }

  reset(): void {
    this.cached.reset()
  }

  get client(): AxiosInstance {
    return this.project.client
  }

  get monitorData(): JSONObject | undefined {
    return this.simulation
  }

  get simulation(): VegetationSimulation | undefined {
    return this.cached.get(`vegetationsimulation/${this.uuid}/`)
  }

  get userData(): VegetationUserData | undefined {
    return this.cached.get(`vegetation/${this.uuid}/`)
  }

  get formPlaceHolder(): VegetationUserData | undefined {
    return this.userData
  }

  async patch(data: VegetationUserData): Promise<AxiosResponse> {
    const promise = this.project.client.patch(`vegetation/${this.uuid}/`, data)
    void promise.then(() => {
      this.reset()
    })
    return await promise
  }
}

export class MultiVegetation implements MultiFeature, WriteFeature {
  public aggregates: LazyAggregates
  private readonly cached: CachedClient
  public readonly isEditingSupported: boolean = true

  constructor(public readonly parent: ParentFeature) {
    makeAutoObservable(this)
    this.aggregates = new LazyAggregates(this, 'vegetationsimulation')
    this.cached = new CachedClient(this)
  }

  reset(): void {
    this.cached.reset()
    this.aggregates.reset()
  }

  get count(): number | undefined {
    return this.aggregates.aggregate({ uuid: 'count' })?.uuid__count
  }

  get client(): AxiosInstance {
    return this.parent.client
  }

  get simulation(): VegetationSimulationAggregate | undefined {
    return this.aggregates.aggregate({
      biogas: 'sum',
      biogas_heat: 'sum',
      biogas_power: 'sum',
      biogas_heat_potential: 'sum',
      biogas_power_potential: 'sum',
      area: 'sum'
    }) as VegetationSimulationAggregate | undefined
  }

  get sample(): Vegetation | undefined {
    return this.cached.get<VegetationSimulation[], Vegetation>(
      `vegetationsimulation/?${this.parent.filter}limit=1`,
      undefined,
      (data) => new Vegetation(this.parent.store.selectedProject, data[0].uuid)
    )
  }

  async patch(data: VegetationUserData): Promise<AxiosResponse> {
    const promise = this.parent.client.patch(
      'vegetation/?' + this.parent.filter,
      data
    )
    void promise.then(() => {
      this.reset()
    })
    return await promise
  }
}

export class VegetationCollection implements ParentFeature, WriteFeature {
  public readonly type = FeatureType.Vegetation

  public vegetations: MultiVegetation
  public store: AppStore
  public readonly isEditingSupported: boolean = true

  constructor(
    private readonly project: Project,
    public uuids: string[]
  ) {
    this.vegetations = new MultiVegetation(this)
    makeAutoObservable(this)
    this.store = project.store
  }

  reset(): void {
    this.vegetations.reset()
  }

  get count(): number | undefined {
    return this.vegetations.count
  }

  get simulation(): VegetationSimulationAggregate | undefined {
    return this.vegetations.simulation
  }

  get client(): AxiosInstance {
    return this.project.client
  }

  get title(): string {
    return `${this.uuids.length} Feldblöcke ausgewählt`
  }

  get filter(): string {
    return `uuid__in=${this.uuids.join(',')}&`
  }

  get monitorData(): JSONObject | undefined {
    return this.vegetations.simulation
  }

  get formPlaceHolder(): JSONObject | undefined {
    const sample = this.vegetations.sample
    if (sample?.simulation === undefined || sample.userData === undefined)
      return
    return { ...sample.simulation, ...sample.userData }
  }

  async patch(data: VegetationUserData): Promise<AxiosResponse> {
    return await this.vegetations.patch(data)
  }
}
